<template>
  <div class="agent-table-container relative">
    <slot name="custom-date-picker" />
    <!-- Fixed height to match the sibling Call History card. -->
    <div
      class="agent-table-wrap relative"
      :style="{ height: tableWrapHeight }"
    >
      <ve-table
        v-show="agentMetrics.length > 0"
        style="height: 100%;"
        :fixed-header="true"
        :columns="columns"
        :table-data="tableData"
        :event-custom-option="bodyRowEventsOption"
      />
      <div
        v-if="!isLoading && !agentMetrics.length"
        class="agent-table-empty-overlay"
      >
        <empty-state
          :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
        />
      </div>
    </div>
    <p
      v-if="agentMetrics.length > 0"
      class="agent-table-row-hint"
    >
      {{ $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.ROW_HINT') }}
    </p>
    <div
      v-if="isLoading"
      class="agents-loader-overlay"
    >
      <div class="agents-loader-content">
        <spinner size="48" />
        <span class="ml-4 font-bold text-base">{{
          $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')
        }}</span>
      </div>
    </div>
    <div v-if="agentMetrics.length > 0" class="table-pagination">
      <ve-pagination
        :total="agents.length"
        :page-index="pageIndex"
        :page-size="25"
        :page-size-option="[25]"
        @on-page-number-change="onPageNumberChange"
      />
    </div>
    <!-- Reserve pagination row height when empty. -->
    <div v-else class="w-full h-[57px]"></div>
  </div>
</template>

<script lang="jsx">
import { h } from 'vue';
import { VeTable, VePagination } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import { formatTime } from '@chatwoot/utils';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

export default {
  name: 'AgentTable',
  components: {
    EmptyState,
    Spinner,
    VeTable,
    VePagination,
  },
  mixins: [rtlMixin],
  props: {
    agents: {
      type: Array,
      default: () => [],
    },
    agentMetrics: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    pageIndex: {
      type: Number,
      default: 1,
    },
    showCustomDatePicker: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      bodyRowEventsOption: Object.freeze({
        bodyRowEvents: ({ row }) => ({
          click: () => this.onRowClick(row),
        }),
      }),
    };
  },
  computed: {
    tableWrapHeight() {
      return this.showCustomDatePicker ? '294px' : '390px';
    },
    tableData() {
      return this.agentMetrics.map(agent => {
        const agentInformation = this.getAgentInformation(agent.id) || {};
        const avgSeconds = agent.metric?.avg_response_time;

        return {
          id: agent.id,
          agent: agentInformation.name || agent.name || 'Unknown',
          email: agentInformation.email || agent.email || 'N/A',
          thumbnail: agentInformation.thumbnail || agent.thumbnail || '',
          completedTasks: agent.metric?.completed_tasks || 0,
          pendingTasks: agent.metric?.pending_tasks || 0,
          agentCalls: agent.metric?.agent_calls || 0,
          answeredCalls: agent.metric?.answered_calls || 0,
          answeredCallsDuration: this.formatCallDuration(
            agent.metric?.answered_calls_duration
          ),
          avgResponseTimeSeconds: avgSeconds,
          avgResponseTime: this.formatAvgResponseTime(avgSeconds),
          status: agentInformation.availability_status || agent.availability || 'Unknown',
        };
      });
    },
    columns() {
      return [
        {
          field: 'agent',
          key: 'agent',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'
          ),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 240,
          ellipsis: { showTitle: false, lineClamp: 1 },
          renderBodyCell: ({ row }) => {
            return h('div', { class: 'row-user-block flex flex-row flex-nowrap items-center text-left w-full min-w-0' }, [
              h('div', { class: 'flex items-center shrink-0' }, [
                h(Thumbnail, {
                  props: {
                    src: row.thumbnail,
                    size: '32px',
                    username: row.agent,
                    status: row.status
                  }
                }),
              ]),
              h('div', { class: 'flex flex-col items-start min-w-0 flex-1 agent-table-agent-text' }, [
                h('h6', { class: 'overflow-hidden whitespace-nowrap text-ellipsis text-sm m-0 leading-[1.2] text-slate-800 dark:text-slate-100 max-w-full' }, row.agent),
                h('span', { class: 'block overflow-hidden whitespace-nowrap text-ellipsis text-xs text-slate-600 dark:text-slate-200 max-w-full' }, row.email)
              ])
            ]);
          }
        },
        {
          field: 'avgResponseTime',
          key: 'avgResponseTime',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AVG_RESPONSE_TIME'
          ),
          align: 'center',
          width: 175,
        },
        {
          field: 'agentCalls',
          key: 'agentCalls',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT_CALLS'
          ),
          align: 'center',
          width: 130,
        },
        {
          field: 'answeredCalls',
          key: 'answeredCalls',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.ANSWERED_CALLS'
          ),
          align: 'center',
          width: 140,
        },
        {
          field: 'answeredCallsDuration',
          key: 'answeredCallsDuration',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.ANSWERED_CALLS_DURATION'
          ),
          align: 'center',
          width: 190,
        },
        {
          field: 'completedTasks',
          key: 'completedTasks',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.COMPLETED_TASKS'
          ),
          align: 'center',
          width: 120,
        },
        {
          field: 'pendingTasks',
          key: 'pendingTasks',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.PENDING_TASKS'
          ),
          align: 'center',
          width: 110,
        },
      ];
    },
  },
  methods: {
    onPageNumberChange(pageIndex) {
      this.$emit('page-change', pageIndex);
    },
    onRowClick(row) {
      if (!row || !row.id) return;
      this.$emit('agent-click', {
        id: Number(row.id),
        name: row.agent,
        avgResponseTime: row.avgResponseTime,
        avgResponseTimeSeconds: row.avgResponseTimeSeconds,
      });
    },
    getAgentInformation(id) {
      return this.agents.find(agent => agent.id === Number(id));
    },
    formatAvgResponseTime(value) {
      if (value === null || value === undefined) {
        return '—';
      }
      return formatTime(value);
    },
    formatCallDuration(seconds) {
      const total = Number(seconds);
      if (!Number.isFinite(total) || total <= 0) return '0s';

      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const secs = Math.floor(total % 60);
      if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
      if (minutes > 0) return `${minutes}m ${secs}s`;
      return `${secs}s`;
    },
  },
};
</script>

<style lang="scss" scoped>
.agent-table-container {
  @apply flex flex-col flex-1;
  position: relative; // Ensure overlay is positioned relative to this container

  :deep(.custom-date-picker-wrapper) {
    padding-bottom: var(--space-small);
    min-height: 100px;
    width: 100%;
    display: block;
  }

  // Fixed-height area; matches .call-history-table-wrap.
  .agent-table-wrap {
    overflow-x: auto;
    overflow-y: hidden;
  }

  .agent-table-wrap :deep(.ve-table-container) {
    overflow-x: hidden !important;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .agent-table-wrap :deep(.ve-table-container::-webkit-scrollbar) {
    width: 0;
    height: 0;
    display: none;
  }

  .agent-table-wrap :deep(.ve-table-body-tr) {
    cursor: pointer;
  }

  .agent-table-empty-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .agent-table-row-hint {
    @apply text-xs text-slate-500 dark:text-slate-400 m-0 mt-2;
  }

  .ve-table {
    &:deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        padding: var(--space-small) var(--space-small) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-small) !important;
      }

      th.ve-table-header-th:not(:first-child) {
        white-space: nowrap;
      }
    }
  }

  :deep(.agent-table-agent-text) {
    margin-left: 8px;
  }

  &:deep(.ve-pagination) {

    @apply bg-transparent dark:bg-transparent;
  }

  &:deep(.ve-pagination-select) {

    @apply hidden;
  }


  .table-pagination {
    @apply mt-4 text-right;
  }
}

.agents-loader-overlay {
  @apply bg-white/90 dark:bg-slate-900/90;
  position: absolute;
  top: 0;
  left: 0;
  // Above ve-table's fixed header (z-index ~5).
  z-index: 10;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agents-loader-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.agents-loader {
  @apply items-center flex text-base justify-center p-8;
}
</style>
